import React from 'react';
import { useWizard, WizardSubStep } from '@zillow/zrs-rental-applications';
import { Anchor, Flex, Heading, IconFaceUnhappy, Paragraph } from '@zillow/constellation';
import { EXPERIAN_CONNECT_FAQ_URL, EXPERIAN_FROZEN_ACCOUNT_URL } from '../../../constants/urls';
import { useIdVerificationCallbacks, useTermSet } from '../../../contexts/IdVerificationContext';
import { ExitReason, TermSet } from '../../../../../models';

interface FrozenAccountProps {
  id: string;
}

function LandlordContent() {
  return (
    <Flex display="flex" flexDirection="column" gap="sm">
      <IconFaceUnhappy size="md" fontColor="blue600" />
      <Heading level={4} as="h2">
        Unable to verify your identity
      </Heading>
      <Paragraph>
        We use Experian’s service to verify your identity. They have indicated that your information is currently
        frozen, which can be a result of a consumer request or security precaution.
      </Paragraph>
      <Paragraph>
        Before we can proceed, you will need to contact Experian to unfreeze your account. Questions?{' '}
        <Anchor href={EXPERIAN_CONNECT_FAQ_URL} target="_blank" rel="noopener noreferrer">
          Learn more about Experian’s identity verification
        </Anchor>
      </Paragraph>
    </Flex>
  );
}

function RenterContent() {
  return (
    <Flex display="flex" flexDirection="column" gap="sm">
      <IconFaceUnhappy size="md" fontColor="blue600" />
      <Heading level={4} as="h2">
        Your credit file is currently frozen
      </Heading>
      <Paragraph>Experian reports that your credit file has been frozen for security reasons.</Paragraph>
      <Paragraph>
        Please contact the{' '}
        <Anchor href={EXPERIAN_FROZEN_ACCOUNT_URL} target="_blank" rel="noopener noreferrer">
          Experian Freeze Center
        </Anchor>{' '}
        to resolve the issue.
      </Paragraph>
    </Flex>
  );
}

export default function FrozenAccount({ id }: FrozenAccountProps) {
  const { currentSubStep } = useWizard();
  const termSet = useTermSet();
  const { onExit } = useIdVerificationCallbacks();
  return (
    <WizardSubStep
      id={id}
      hidden={currentSubStep !== id}
      meta={{
        renderNextButton: 'Done',
        renderBackButton: () => null,
      }}
      onRequestNext={(abortController: AbortController) => {
        abortController.abort();
        onExit({ reason: ExitReason.FrozenAccount, success: false });
      }}
      render={termSet === TermSet.LandlordScreening ? LandlordContent : RenterContent}
    />
  );
}
